import { createStore, createEffect, createEvent, combine } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import { ProfileApi, RequestStrategyEnum, UsersApi, type ProfileResponse, type UpdateUserRequest } from '@shared/api';

const getInitialProfileEffect = createEffect(() => ProfileApi.getProfile());
const getProfileEffect = createEffect(() => ProfileApi.getProfile());
const updateProfileEffect = createEffect((request: UpdateUserRequest) => UsersApi.updateUser(request));
const clearEvent = createEvent();

const $dataStore = createStore<ProfileResponse | null>(null)
  .on(getInitialProfileEffect.done, (_, data) => data.result.data || null)
  .on(getProfileEffect.done, (current, data) => data.result.data || current)
  .on(updateProfileEffect.done, (current, data) => {
    if (!current || !data.result.data) return current;
    return { ...current, ...data.result.data };
  })
  .reset(clearEvent);

const $loadedStore = createStore<boolean>(false)
  .on(getInitialProfileEffect.done, () => true)
  .on(getInitialProfileEffect.fail, () => true)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getInitialProfileEffect.pending, (_, data) => data)
  .on(getInitialProfileEffect.fail, () => false)
  .on(getInitialProfileEffect.done, () => false)
  .on(getProfileEffect.pending, (_, data) => data)
  .on(getProfileEffect.fail, () => false)
  .on(getProfileEffect.done, () => false)
  .reset(clearEvent);

const $actionLoadingStore = createStore<boolean>(false)
  .on(updateProfileEffect.pending, (_, data) => data)
  .on(updateProfileEffect.fail, () => false)
  .on(updateProfileEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getInitialProfileEffect, () => null)
  .on(getInitialProfileEffect.fail, () => 'Something went wrong while fetching Profile. Please try again.')
  .on(getInitialProfileEffect.done, () => null)
  .on(getProfileEffect, () => null)
  .on(getProfileEffect.fail, () => 'Something went wrong while fetching Profile. Please try again.')
  .on(getProfileEffect.done, () => null)
  .reset(clearEvent);

const $actionErrorStore = createStore<string | null>(null)
  .on(updateProfileEffect, () => null)
  .on(
    updateProfileEffect.fail,
    (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while updating Profile. Please try again.').message,
  )
  .on(updateProfileEffect.done, () => null)
  .reset(clearEvent);
$actionErrorStore.watch((error) => $notificationStore.events.setError(error));

export const $profileStore = {
  events: {
    getInitialProfile: ApiUtils.wrapperApi(getInitialProfileEffect, RequestStrategyEnum.Debounce).trigger,
    getProfile: getProfileEffect,
    updateProfile: updateProfileEffect,
    clear: clearEvent,
  },
  store: combine({
    loaded: $loadedStore,
    loading: $loadingStore,
    actionLoading: $actionLoadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
